import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientsQueryResponse, GetAllClientsQueryParams } from "../types/GetAllClients";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientsClient = typeof client<GetAllClientsQueryResponse, never, never>;
type GetAllClients = {
    data: GetAllClientsQueryResponse;
    error: never;
    request: never;
    pathParams: never;
    queryParams: GetAllClientsQueryParams;
    headerParams: never;
    response: GetAllClientsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientsClient>[0]>;
        return: Awaited<ReturnType<GetAllClientsClient>>;
    };
};
export const getAllClientsQueryKey = (params?: GetAllClients["queryParams"]) => [{ url: "/api/clients" }, ...(params ? [params] : [])] as const;
export type GetAllClientsQueryKey = ReturnType<typeof getAllClientsQueryKey>;
export function getAllClientsQueryOptions(params?: GetAllClients["queryParams"], options: GetAllClients["client"]["parameters"] = {}) {
    const queryKey = getAllClientsQueryKey(params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClients["data"], GetAllClients["error"]>({
                method: "get",
                url: `/api/clients`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all clients the current identity user has access to. For
example, if a logged in user calls this API then it will be all the clients
that user can access. If it is called via an API Key then it will be all
the clients that API Key can access.
 * @summary Get all
 * @link /api/clients
 */
export function useGetAllClients<TData = GetAllClients["response"], TQueryData = GetAllClients["response"], TQueryKey extends QueryKey = GetAllClientsQueryKey>(params?: GetAllClients["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClients["response"], GetAllClients["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClients["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClients["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientsQueryKey(params);
    const query = useQuery({
        ...getAllClientsQueryOptions(params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClients["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientsSuspenseQueryKey = (params?: GetAllClients["queryParams"]) => [{ url: "/api/clients" }, ...(params ? [params] : [])] as const;
export type GetAllClientsSuspenseQueryKey = ReturnType<typeof getAllClientsSuspenseQueryKey>;
export function getAllClientsSuspenseQueryOptions(params?: GetAllClients["queryParams"], options: GetAllClients["client"]["parameters"] = {}) {
    const queryKey = getAllClientsSuspenseQueryKey(params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClients["data"], GetAllClients["error"]>({
                method: "get",
                url: `/api/clients`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all clients the current identity user has access to. For
example, if a logged in user calls this API then it will be all the clients
that user can access. If it is called via an API Key then it will be all
the clients that API Key can access.
 * @summary Get all
 * @link /api/clients
 */
export function useGetAllClientsSuspense<TData = GetAllClients["response"], TQueryKey extends QueryKey = GetAllClientsSuspenseQueryKey>(params?: GetAllClients["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClients["response"], GetAllClients["error"], TData, TQueryKey>>;
    client?: GetAllClients["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClients["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientsSuspenseQueryKey(params);
    const query = useSuspenseQuery({
        ...getAllClientsSuspenseQueryOptions(params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClients["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}